import { EditIcon, ProductOptionsIcon, TrashIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import React from "react";
import { Event, EventOptionSource, Experience, ExperienceOption, Permission } from "types";
import { IsGranted } from "./IsGranted";
import { NoTranslate } from "./NoTranslate";
import { SecondaryButton } from "./SecondaryButton";
import { ApplyExperienceOptionModal } from "@/components/Event/ApplyExperienceOptionModal";
import { RemoveExperienceOptionModal } from "@/components/Event/RemoveExperienceOptionModal";
import { getScheduleSummary } from "@/helpers/scheduleSummaryHelper";
import { useModal } from "@/hooks/useVisibility";
import { getEventOptionRow, isFutureEvent, isManuallyRemoved } from "@/models/Event";
import { isOptionsEnabled } from "@/models/Experience";

interface EventOptionRowProps {
    event: Event;
    experience?: Experience;
    option: ExperienceOption | null;
}

const OptionTooltip = ({ event, experience }: { event: Event; experience?: Experience }) => {
    const row = getEventOptionRow(event);

    if (row?.source === EventOptionSource.EVENT) {
        return <span>Manually applied to this event and not linked to a recurring schedule.</span>;
    }

    const matchingSchedules = experience?.schedules?.filter((schedule) =>
        schedule.options?.some((scheduleOption) => scheduleOption.optionId === row?.optionId),
    );

    // A reusable option can attach to several schedules; only name one when unambiguous.
    return matchingSchedules?.length === 1 ? (
        <span>Inherited from schedule: {getScheduleSummary(matchingSchedules[0], true)}</span>
    ) : (
        <span>Inherited from this event&apos;s schedule.</span>
    );
};

export const EventOptionRow = ({ event, experience, option }: EventOptionRowProps) => {
    const applyModal = useModal(false);
    const removeModal = useModal(false);

    if (experience && !isOptionsEnabled(experience)) {
        return null;
    }

    const isFuture = isFutureEvent(event);

    if (!option) {
        if (!isFuture) {
            return null;
        }

        const applyButton = (
            <button
                type="button"
                className="underline"
                data-testid="event-option-apply-button"
                onClick={applyModal.show}
            >
                Apply Experience Option
            </button>
        );

        return (
            <IsGranted permissions={Permission.MODIFY_BOOKING}>
                <div className="flex items-center space-x-2" data-testid="event-option-row">
                    <ProductOptionsIcon className="h-4 w-4 shrink-0" />
                    {isManuallyRemoved(event) ? (
                        <Tooltip content="Manually removed from this event">{applyButton}</Tooltip>
                    ) : (
                        applyButton
                    )}
                    <ApplyExperienceOptionModal
                        events={[event]}
                        isOpen={applyModal.isVisible}
                        onClose={applyModal.hide}
                    />
                </div>
            </IsGranted>
        );
    }

    return (
        <div className="flex items-center space-x-2" data-testid="event-option-row">
            <ProductOptionsIcon className="h-4 w-4 shrink-0" />
            <Tooltip content={<OptionTooltip event={event} experience={experience} />}>
                <span className="underline" data-testid="event-option-name">
                    <NoTranslate>{option.name}</NoTranslate>
                </span>
            </Tooltip>
            {isFuture && (
                <IsGranted permissions={Permission.MODIFY_BOOKING}>
                    <SecondaryButton size="tiny" data-testid="event-option-edit-button" onClick={applyModal.show}>
                        <EditIcon />
                    </SecondaryButton>
                    <SecondaryButton size="tiny" data-testid="event-option-remove-button" onClick={removeModal.show}>
                        <TrashIcon />
                    </SecondaryButton>
                    <ApplyExperienceOptionModal
                        events={[event]}
                        isOpen={applyModal.isVisible}
                        onClose={applyModal.hide}
                    />
                    <RemoveExperienceOptionModal
                        events={[event]}
                        isOpen={removeModal.isVisible}
                        onClose={removeModal.hide}
                    />
                </IsGranted>
            )}
        </div>
    );
};
